import {
  AuthClient,
  AuthClientOptions,
  AuthenticationError,
  RedirectLoginResult,
} from '@adsk/identity-web-sdk';
import jwt_decode, { JwtPayload } from 'jwt-decode';

import {
  AURR_STATE_STORAGE_KEY,
  AUTH_RECOVERABLE_ERRORS,
  authInitialState,
} from './constants';
import {
  cleanupAuthQueryParams,
  getAuthState,
  hasAuthQueryParams,
  setAuthState,
} from './utils';
import { buildCacheKey, getAxiosInstance } from '../../../apis/axiosCache';
import { cacheTTLs } from '../../../common/constants';
import { accountSchema } from '../../../common/schema';
import { Account } from '../../../common/type';
import { logZodError } from '../../../helpers/log-zod-error';
import { displatchIdentityContextEvent } from '../../events';

let currentAuthClient: AuthClient | null = null;

/**
 * Save the current URL as the return URL for the next login.
 * This is used to redirect the user to the page they were on before logging in.
 * This fix is to patch the issue with the Auth SDK not supporting the `appState` parameter.
 *
 * @returns void
 */
function saveReturnUrl() {
  try {
    const storageState = localStorage.getItem(AURR_STATE_STORAGE_KEY);
    const existingState =
      typeof storageState === 'string' ? JSON.parse(storageState) : {};
    localStorage.setItem(
      AURR_STATE_STORAGE_KEY,
      JSON.stringify({
        ...existingState,
        returnUrl: window.location.href,
      })
    );
  } catch (error) {
    // Ignore cross-origin errors
    console.warn('[AuthProvider] Could not save return URL:', error);
  }
}

/**
 * Get the return URL from the local storage.
 * This is used to redirect the user to the page they were on before logging in.
 * This fix is to patch the issue with the Auth SDK not supporting the `appState` parameter.
 *
 * @returns string
 */
function getReturnUrl() {
  let returnUrl = '';
  const storageState = localStorage.getItem(AURR_STATE_STORAGE_KEY);
  const existingState =
    typeof storageState === 'string' ? JSON.parse(storageState) : null;

  if (existingState?.returnUrl) {
    returnUrl = existingState.returnUrl;
    delete existingState.returnUrl;
    localStorage.setItem(AURR_STATE_STORAGE_KEY, JSON.stringify(existingState));
  }

  return returnUrl;
}

export async function authHandleRedirectCallback(): Promise<RedirectLoginResult> {
  if (!currentAuthClient) {
    // eslint-disable-next-line no-console
    console.error(
      "[AuthProvider] Can't handle the redirect callback without an auth client."
    );
    return {
      appState: undefined,
    };
  }
  try {
    return await currentAuthClient.handleRedirectCallback();
  } catch (error) {
    throw new Error(`[AuthProvider] Failed to handle the redirect callback.`, {
      cause: error as Error,
    });
  }
}

export async function authGetUserProfile(
  access_token: string,
  env = 'prod'
): Promise<Account> {
  const envUrl =
    env === 'prod' || env === 'test'
      ? 'https://developer.api.autodesk.com'
      : 'https://developer-stg.api.autodesk.com';

  const axiosConfig = {
    baseURL: envUrl,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
    },
    id: buildCacheKey(['aurr', 'profileinfo']),
    cache: {
      ttl: cacheTTLs.userProfile,
    },
  };

  const api = getAxiosInstance();

  const { data } = await api.get(`/userinfo`, axiosConfig);

  const responseData = accountSchema.safeParse(data);
  logZodError(responseData);

  return data as Account;
}
export async function getSafeAccessToken(): Promise<string | null> {
  if (!currentAuthClient) {
    console.warn('[AuthProvider] No auth client available');
    return null;
  }

  try {
    // First check if user is authenticated
    const isAuthenticated = await currentAuthClient.isAuthenticated();
    if (!isAuthenticated) {
      return null;
    }

    // Try to get token silently
    const token = await currentAuthClient.getAccessTokenSilently();
    return token;
  } catch (error: any) {
    console.warn(
      '[AuthProvider] Silent token retrieval failed:',
      error?.message || error
    );

    // If it's a CORS/cross-origin error, handle gracefully
    if (
      error?.message?.includes('cross-origin') ||
      error?.message?.includes('CORS') ||
      error?.name === 'SecurityError'
    ) {
      console.info(
        '[AuthProvider] CORS error detected, will require re-authentication'
      );

      // Optionally trigger a re-authentication flow
      // You might want to set a flag here to prompt user to re-login
      return null;
    }

    // Re-throw other types of errors
    throw error;
  }
}
export async function authCheckSession(
  env: string,
  returnURL?: string
): Promise<void> {
  if (window.self !== window.top) {
    return;
  }

  if (window.isCheckingSession) {
    console.error('[AuthProvider] Already checking the session.');
    return;
  }

  let newState = {
    ...authInitialState,
    ...getAuthState(),
    loginEnv: env,
    userIsLoading: false,
  };

  if (!currentAuthClient) {
    console.error(
      "[AuthProvider] Can't check the session without an auth client."
    );
    return;
  }

  window.isCheckingSession = true;

  try {
    const isLoggedIn = await currentAuthClient.isAuthenticated();
    const access_token = await getSafeAccessToken();

    newState.isLoggedIn = isLoggedIn && !!access_token;

    if (isLoggedIn && access_token) {
      newState.access_token = access_token;
      const claims: JwtPayload = jwt_decode(access_token);
      newState.expires_on = claims?.exp
        ? claims.exp * 1000
        : Date.now() + 60 * 60 * 1000;

      try {
        const user = await authGetUserProfile(access_token, newState.loginEnv);
        if (user) {
          newState.userProfile = user;
        }
      } catch (profileError) {
        console.warn(
          '[AuthProvider] Failed to get user profile:',
          profileError
        );
        // Continue without profile if it fails
      }
    }
  } catch (e) {
    const error = e as AuthenticationError;
    if (error && AUTH_RECOVERABLE_ERRORS.includes(error.error)) {
      newState = { ...authInitialState, loginEnv: newState.loginEnv };
    } else {
      throw new Error(`[AuthProvider] Failed to check the session.`, {
        cause: error,
      });
    }
  } finally {
    setAuthState(newState);
    displatchIdentityContextEvent(newState);
    window.isCheckingSession = false;

    if (returnURL) {
      window.location.assign(returnURL);
    }
  }
}

export async function authInitClient({
  cacheLocation = 'sessionstorage',
  env = 'prod',
  scope = 'data:read',
  isRedirectPage = false,
  ...options
}: AuthClientOptions & { isRedirectPage?: boolean }): Promise<void> {
  if (currentAuthClient) {
    // eslint-disable-next-line no-console
    console.error('[AuthProvider] Auth client already initialized.');
    return;
  }

  try {
    currentAuthClient = new AuthClient({
      cacheLocation,
      env,
      scope,
      ...options,
    });

    let returnURL = getReturnUrl();
    if (hasAuthQueryParams()) {
      try {
        const callbackHandle = await authHandleRedirectCallback();
        returnURL = callbackHandle?.appState?.returnURL || returnURL;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          '[AuthProvider] Error handling redirect callback:',
          error
        );
      } finally {
        cleanupAuthQueryParams();
      }
    } else if (isRedirectPage) {
      let homepageRedirect = window.location.origin;
      if (window.location.pathname.includes('/autodesk-university')) {
        homepageRedirect += '/autodesk-university';
      }
      window.location.assign(homepageRedirect);
      return; // Don't continue if we're redirecting
    }

    await authCheckSession(env, returnURL);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[AuthProvider] Error initializing auth client:', error);
    // Reset the client on error
    currentAuthClient = null;
  }
}

export async function authLogout(): Promise<void> {
  if (window.isLoggingOut) {
    return;
  }

  window.isLoggingOut = true;
  localStorage.removeItem(AURR_STATE_STORAGE_KEY);

  try {
    const axiosInstance = getAxiosInstance();
    await axiosInstance.storage.remove(buildCacheKey(['aurr', 'profileinfo']));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[AuthProvider] Failed to invalidate profile cache');
  }

  if (!currentAuthClient) {
    window.isLoggingOut = false;
    // eslint-disable-next-line no-console
    console.error("[AuthProvider] Can't logout without an auth client.");
    return;
  }

  await currentAuthClient.logout({
    post_logout_redirect_uri: window.location.origin + window.location.pathname,
  });

  window.isLoggingOut = false;
}

export async function authLogin(returnUrl?: string): Promise<void> {
  if (window.isLoggingIn) {
    return;
  }
  window.isLoggingIn = true;

  if (!currentAuthClient) {
    window.isLoggingIn = false;
    // eslint-disable-next-line no-console
    console.error("[AuthProvider] Can't login without an auth client.");
    return;
  }

  saveReturnUrl();

  await currentAuthClient.loginWithRedirect({
    appState: {
      returnURL: returnUrl || window.location.href,
    },
  });

  window.isLoggingIn = false;
}
